<template>
    <div
        class="l-ss-c-offer-params"
        v-if="allParams.length">
        <offer-params-wrapper
            v-for="([title, items]) in allParams"
            :key="title"
            :title="title"
            :items="items"
            :available-items="availableParams[title]"
            v-model="selectedParams[title]"
            @change="changeOffer({ title, item: $event })"/>
    </div>
</template>

<script>
    import { ref, computed } from '@vue/composition-api';
    import OfferParamsWrapper from '@/core/components/offer/offer-params/offer-params-wrapper.vue';

    import useOfferParamsTree from '@/core/components/offer/offer-params/use-offer-params-tree';

    export default {
        components: { OfferParamsWrapper },
        props: {
            offer: {
                type: Object,
            },
            offerSettings: {
                type: Object,
            },
            itemSettings: {
                type: Object,
            },
        },
        setup(props, { emit }) {
            const { offer, offerSettings, itemSettings } = props;

            const memo = computed(() => props.offerSettings.memo.offerParamsMemo || {});

            const updateMemo = (key, value) => {
                emit('change-memo', { key, value });
            };

            const selectedGroupedParams = offer.selectedGroupedParams || {};

            const groupedParams = ref(offer.groupedParams || {});
            const selectedParams = ref(selectedGroupedParams);

            let allParams = memo.value.allParams || [];
            let availableParams = ref({});
            let getOfferByParams = memo.value?.getOfferByParams || null;

            const sortParamValues = (paramName, values) => {
                const groupedParamsConfig = offerSettings?.groupedParamsConfig || {};
                const customSort = groupedParamsConfig.customSort || {};
                const globalSortOrder = groupedParamsConfig.globalSortOrder || 'ASC';
                const sortOrder = customSort[paramName] || globalSortOrder;
                const validSortOrders = ['ASC', 'DESC'];

                if (typeof sortOrder === 'function') {
                    return values.toSorted(sortOrder);
                }

                if (!validSortOrders.includes(sortOrder)) {
                    return values;
                }

                return values.toSorted((a, b) => {
                    const numA = parseFloat(a);
                    const numB = parseFloat(b);

                    let comparison;

                    if (!isNaN(numA) && !isNaN(numB)) {
                        comparison = numA - numB;
                    } else {
                        comparison = a.toString().localeCompare(b.toString());
                    }

                    return sortOrder === 'DESC' ? -comparison : comparison;
                });
            };

            if (groupedParams.value && !memo.value.allParams) {
                const orderForGroupedParamNames = offerSettings.orderForGroupedParamNames;
                const sortedGroupedParams = {};
                Object.entries(groupedParams.value).forEach(([paramName, paramData]) => {
                    sortedGroupedParams[paramName] = sortParamValues(paramName, paramData);
                });

                allParams = Object.entries(sortedGroupedParams);

                if (itemSettings.include) {
                    allParams = allParams.filter(([paramName]) => {
                        return itemSettings.include.includes(paramName);
                    });
                }

                allParams.sort((a, b) => {
                    const aIndex = orderForGroupedParamNames.indexOf(a[0]);
                    const bIndex = orderForGroupedParamNames.indexOf(b[0]);
                    const NOT_FOUND_INDEX = 999;
                    const aOrder = aIndex === -1 ? NOT_FOUND_INDEX : aIndex;
                    const bOrder = bIndex === -1 ? NOT_FOUND_INDEX : bIndex;

                    if (aOrder !== bOrder) {
                        return aOrder - bOrder;
                    }
                    return a[0].localeCompare(b[0]);
                });

                updateMemo('offerParamsMemo.allParams', allParams);
            }

            if (memo.value.availableParams) {
                availableParams.value = memo.value.availableParams;
            } else {
                Object.entries(groupedParams.value).forEach(([title, offers]) => {
                    availableParams.value[title] = offers;
                });
                updateMemo('offerParamsMemo.availableParams', availableParams.value);
            }

            const processParams = (currentSelectedParam) => {
                const {
                    newSelectedParams,
                    newAvailableParams,
                    newOffer,
                } = getOfferByParams(allParams, selectedParams.value, currentSelectedParam);

                selectedParams.value = newSelectedParams;
                availableParams.value = newAvailableParams;

                updateMemo('offerParamsMemo.availableParams', availableParams.value);

                return newOffer;
            };

            const changeOffer = async (currentParam) => {
                if (!memo.value.groupedOffers?.length) {
                    await loadAllIntersections();
                }

                selectedParams.value[currentParam.title] = currentParam.item;

                const newOffer = processParams(currentParam);

                emit('change-offer', newOffer);
            };

            const loadAllIntersections = async () => {
                return new Promise((resolve) => {
                    emit('load-grouped-offers', {
                        offer, callback: (offers) => {
                            onLoadGroupedOffers(offers);
                            resolve();
                        },
                    });
                });
            };

            const onLoadGroupedOffers = (offers) => {
                getOfferByParams = useOfferParamsTree(offers);
                updateMemo('offerParamsMemo.groupedOffers', offers);
                updateMemo('offerParamsMemo.getOfferByParams', getOfferByParams);
            };

            return {
                allParams,
                selectedParams,
                groupedParams,
                availableParams,
                changeOffer,
            };
        },
    };

</script>
